import { useApiRef } from '@/components/Functional/hooks-core/useApi'
import {
  useTanstackQuestionnairesTypeFetchFn,
  useTanstackRewardDetailFetchFn,
  useTanstackUserRewardDetailFetchFn,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { mergeRewardDetail } from '@/components/Functional/lib/api/rewards'
import { ApiError } from '@/components/Functional/lib/request'
import type {
  ApplicationFormData,
  MergedRewardData,
  RewardGetResponse,
  UserRewardStatusDetailData,
  UserRewardStatusGetResponse,
  UserRewardTransactionData,
  UserRewardTransactionGetResponse,
} from '@/schemas/v2'
import { UserRewardTransactionGetResponseSchema } from '@/schemas/v2'
import { type PhotoFrameItem, toPhotoFrameItems } from '@/schemas/v2/photo-frame'
import { useTokenStore } from '@/store/useTokenStore'
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useEffect, useMemo, useRef } from 'react'
import { assign, fromPromise, setup } from 'xstate'

// ---------------------------------------------------------------------------
// Context / Event 型
// ---------------------------------------------------------------------------

export type RewardFlowContext = {
  rewardId: number
  isAuthenticated: boolean
  reward: MergedRewardData | null
  userRewardStatus: UserRewardStatusDetailData | null
  transaction: UserRewardTransactionData | null
  /** 応募フォーム定義。applicationForm フローを通る場合に設定される */
  applicationForm: ApplicationFormData | null
  /** 応募フォーム送信先の userRewardTransactionId */
  applicationFormTransactionId: number | null
  /**
   * 一覧ダイアログで選択した賞品の userRewardTransactionId。
   * 複数賞品（isRepeatable=true）のうち特定の行を交換するときに使用する。
   * exchange 後は initialLoading でリセットされる。
   */
  selectedTransactionId: number | null
  /** 応募フォームの入力値（確認画面表示用） */
  pendingFormData: Record<string, string | string[]> | null
  pendingSpotId: number | null
  /**
   * スポット指定クーポン消込（POST /api/v2/coupons/{id}）を完了済みか。
   * 後段（exchange）失敗時のリトライで二重消込が起きないよう、完了後は true にしてスキップする。
   */
  couponRedeemDone: boolean
  errorMessage: string | null
  /**
   * サーバーサイドのバリデーションエラー（422 レスポンスの errors フィールド）。
   * キーは fieldKey、値はエラーメッセージ文字列。
   * 422 エラー時に applicationForm ステートに戻る際にセットされ、
   * 再送信（APPLICATION_FORM_SUBMIT）時にクリアされる。
   */
  serverValidationErrors: Record<string, string> | null
  completedVia: 'application' | 'exchange' | null
  /** ARフォトフレームデータ。arNavigate 遷移時に fetch して設定。arNavigate 離脱時にリセット */
  arPhotoFrames: PhotoFrameItem[]
  /**
   * ARフォトフレームの fetch が完了したか（成功・失敗問わず）。
   * PhotoFrame は mount 時に一度だけフレームを読み込むため、
   * fetch 完了後に ArPhotoOverlay を開くことで空フレームでの mount を防ぐ。
   */
  arPhotoFramesReady: boolean
}

export type RewardFlowEvent =
  | { type: 'ACQUIRE_BUTTON_PRESSED'; transactionId?: number }
  | { type: 'QUESTIONNAIRE_ANSWERED' }
  | { type: 'QUESTIONNAIRE_DISMISSED' }
  | { type: 'STAMP_CONSUMPTION_CONFIRMED' }
  | { type: 'DLC_DOWNLOAD' }
  | { type: 'AR_CAMERA_START' }
  | { type: 'ITEM_CONFIRM' }
  | { type: 'APPLICATION_FORM_SUBMIT'; formData: Record<string, string | string[]> }
  | { type: 'RETRY_APPLICATION_FORM' }
  | { type: 'APPLICATION_FORM_CONFIRM' }
  | { type: 'COUPON_SPOT_SELECTED'; spotId: number }
  | { type: 'MANUAL_SPOT_SELECT_OPENED' }
  | { type: 'COUPON_EXCHANGE' }
  | { type: 'EXCHANGE_CONFIRMED' }
  | { type: 'AR_OVERLAY_CLOSED' }
  | { type: 'DIALOG_CLOSED' }
  | { type: 'RETRY' }
  | { type: 'LOTTERY_RECEIVE' }
  /**
   * 抽選結果一覧 / 獲得賞品一覧ダイアログ内の「賞品を受け取る」ボタン押下。
   * prizes[] から特定の transactionId に紐づく賞品を取り出し、
   * POST /api/v2/rewards/{id}（新規獲得）を経由せずに直接 exchange フローへ遷移する。
   */
  | { type: 'DIALOG_EXCHANGE_PRESSED'; transactionId: number }
  | { type: 'LOADED'; reward: MergedRewardData | null; userRewardStatus: UserRewardStatusDetailData | null }
  | { type: 'ACQUIRED'; transaction: UserRewardTransactionData }
  | { type: 'EXCHANGED' }
  | { type: 'ERROR'; message: string }

// ---------------------------------------------------------------------------
// Actor input 型
// ---------------------------------------------------------------------------

type AcquireInput = {
  rewardId: number
  /** LOTTERY型のとき true。最小表示時間の制御に使用する */
  isLottery: boolean
}
type RedeemCouponInput = {
  /** CouponConfig.id（POST /api/v2/coupons/{id} のパスパラメータ） */
  couponConfigId: number
  spotId: number | null
}
type ExchangeInput = {
  spotId: number | null
  transactionId?: number | null
}

type SubmitApplicationFormInput = {
  transactionId: number | null
  formData: Record<string, string | string[]> | null
}

type FetchPhotoFramesInput = {
  transactionId: number | null
}

// ---------------------------------------------------------------------------
// エラーメッセージヘルパー
// ---------------------------------------------------------------------------

/**
 * exchange エラーを日本語メッセージに変換する。
 * ApiError は status を問わずフレンドリーなメッセージに変換し、"HTTP xxx" をユーザーに見せない。
 */
function resolveExchangeErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return '特典が見つかりませんでした。'
    if (error.status === 400) return '現在この特典は受け取れません。'
    if (error.status === 403) return '特典の受け取り権限がありません。'
    if (error.status === 401) return 'セッションが切れました。再度ログインしてください。'
    // 500 系や未知のステータスも生の "HTTP xxx" は見せない
    return '交換処理に失敗しました。しばらく待ってから再試行してください。'
  }
  if (error instanceof Error) return error.message
  return '交換処理に失敗しました。'
}

function resolveApplicationFormErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return '応募先の特典が見つかりませんでした。'
    if (error.status === 400) return '入力内容に誤りがあります。再度ご確認ください。'
    if (error.status === 403) return '応募フォームの送信権限がありません。'
    if (error.status === 401) return 'セッションが切れました。再度ログインしてください。'
    return '応募フォームの送信に失敗しました。しばらく待ってから再試行してください。'
  }
  if (error instanceof Error) return error.message
  return '応募フォームの送信に失敗しました。'
}

/**
 * 422 レスポンスボディからフィールドエラーマップを取り出すヘルパー。
 * バックエンドのフォーマット:
 *   { "message": "...", "errors": { "fieldKey": "エラーメッセージ" }, "unknownFields": [] }
 * 配列・null・値が string 以外のケースは除外し、Record<string, string> のみ返す。
 */
function extractValidationErrors(error: unknown): Record<string, string> | null {
  if (!(error instanceof ApiError)) return null
  if (error.status !== 422) return null
  const body = error.body as { errors?: unknown } | undefined
  const errors = body?.errors
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(errors)) {
    if (typeof value === 'string') {
      result[key] = value
    }
  }
  return Object.keys(result).length > 0 ? result : null
}

/**
 * スポット指定クーポンの couponConfigId をコンテキストから解決するヘルパー。
 * acquire 直後は transaction.prize.couponConfigId、
 * ページリロード後は userRewardStatus.prizes から探索する。
 */
function resolveCouponConfigId(context: RewardFlowContext): number | null {
  return (
    (context.transaction?.prize?.type === 'COUPON' && context.transaction.prize.couponSpecificSpots
      ? context.transaction.prize.couponConfigId
      : null) ??
    (context.userRewardStatus?.prizes ?? []).find(
      (p) =>
        !p.isLotteryLost &&
        p.prize?.type === 'COUPON' &&
        p.prize?.couponSpecificSpots &&
        (context.selectedTransactionId == null || p.userRewardTransactionId === context.selectedTransactionId),
    )?.prize?.couponConfigId ??
    null
  )
}

/**
 * 一覧ダイアログの DIALOG_EXCHANGE_PRESSED イベント処理で使用するヘルパー。
 * context.userRewardStatus.prizes から transactionId に一致するエントリを返す。
 */
function findDialogPrize(context: RewardFlowContext, transactionId: number) {
  return (context.userRewardStatus?.prizes ?? []).find((p) => p.userRewardTransactionId === transactionId)
}

/**
 * ゲスト向け特典詳細を取得し、404 の場合はキャッシュを `{ reward: null }` に更新して null を返す。
 * Issue #2866: キャッシュが残っていても期間外を正しく検知するため、30秒以内はキャッシュを再利用し、30秒超え（stale）の場合はネットワークフェッチして 404 を正しく検知する。
 *
 * @export テスト用にエクスポート（setQueryData の挙動を直接検証するため）
 */
export async function fetchRewardDetailOrInvalidate(
  queryClient: QueryClient,
  queryKey: string[],
  queryFn: () => Promise<RewardGetResponse | undefined>,
): Promise<RewardGetResponse | null> {
  return queryClient
    .fetchQuery<RewardGetResponse | undefined>({
      queryKey,
      queryFn,
      staleTime: 30 * 1000,
      retry: (failureCount: number, error: unknown) =>
        failureCount < 3 && !(error instanceof ApiError && error.status === 404),
    })
    .then((data) => data ?? null)
    .catch((e: unknown) => {
      if (e instanceof ApiError && e.status === 404) {
        // 404 時はキャッシュを { reward: null } に更新する。
        // これにより useQuery が購読している rewardDetailData も null に反映され、
        // freshReward が null になって isPeriodOut が正しく true になる。
        queryClient.setQueryData<RewardGetResponse>(queryKey, { reward: null })
        return null
      }
      throw e
    })
}

export const rewardFlowMachineBase = setup({
  types: {
    context: {} as RewardFlowContext,
    events: {} as RewardFlowEvent,
    input: {} as { rewardId: number; isAuthenticated: boolean },
  },
  actors: {
    loadData: fromPromise<
      { reward: MergedRewardData | null; userRewardStatus: UserRewardStatusDetailData | null },
      void
    >(async () => ({ reward: null, userRewardStatus: null })),
    acquireReward: fromPromise<UserRewardTransactionGetResponse | null, AcquireInput>(async () => null),
    redeemCoupon: fromPromise<void, RedeemCouponInput>(async () => undefined),
    exchangeReward: fromPromise<void, ExchangeInput>(async () => undefined),
    exchangeArPhoto: fromPromise<void, FetchPhotoFramesInput>(async () => undefined),
    submitApplicationForm: fromPromise<void, SubmitApplicationFormInput>(async () => undefined),
    fetchRewardPhotoFrames: fromPromise<PhotoFrameItem[], FetchPhotoFramesInput>(async () => []),
  },
  guards: {
    isNotAuthenticated: ({ context }) => !context.isAuthenticated,
    isAchievableOrAchieved: ({ context }) =>
      context.userRewardStatus?.status === 'ACHIEVABLE' ||
      (context.userRewardStatus?.status === 'ACHIEVED' && context.userRewardStatus?.prizeSelectionType !== 'LOTTERY'),
    /**
     * ARフォトを獲得済みで再表示（ARカメラ起動）可能な場合。
     * 同一セッション内は context.transaction.prize.type で判定。
     * ページリロード後は context.userRewardStatus.prizes から AR_PHOTO を検出。
     * ACHIEVED・EXCHANGED いずれも対象（EXCHANGED は #3127 対応でボタンが活性化された）。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    isAchievedArPhotoWithContext: ({ context }) => {
      if (context.userRewardStatus?.status === 'ACHIEVABLE') return false
      if (context.transaction?.prize?.type === 'AR_PHOTO') return true
      const status = context.userRewardStatus?.status
      return (
        (status === 'ACHIEVED' || status === 'EXCHANGED') &&
        (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'AR_PHOTO')
      )
    },
    /**
     * DLCを獲得済み（ACHIEVED または EXCHANGED）で再表示可能な場合。
     * 再POSTせず直接 dlcAcquired に遷移させる。
     * ページリロード後は context.userRewardStatus.prizes から DLC を検出。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    isAchievedDlcWithContext: ({ context }) => {
      if (context.userRewardStatus?.status === 'ACHIEVABLE') return false
      if (context.transaction?.prize?.type === 'DLC') return true
      const status = context.userRewardStatus?.status
      return (
        (status === 'ACHIEVED' || status === 'EXCHANGED') &&
        (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'DLC')
      )
    },
    /**
     * クーポンを獲得済み（ACHIEVED）で再表示可能な場合。
     * 再POSTせず直接 couponUse に遷移させる。
     * ページリロード後は context.userRewardStatus.prizes から COUPON を検出。
     * couponSpecificSpots=true はスポット選択フロー（couponSpotSelect）で扱うため除外。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    isAchievedCouponWithContext: ({ context }) => {
      if (context.userRewardStatus?.status === 'ACHIEVABLE') return false
      if (context.transaction?.prize?.type === 'COUPON' && !context.transaction.prize.couponSpecificSpots) return true
      return (
        context.userRewardStatus?.status === 'ACHIEVED' &&
        (context.userRewardStatus?.prizes ?? []).some(
          (p) => p.prize?.type === 'COUPON' && !p.prize?.couponSpecificSpots,
        )
      )
    },
    /**
     * クーポンを獲得済み（ACHIEVED）かつ couponSpecificSpots=true（スポット指定クーポン）の場合。
     * スポット選択ダイアログ（couponSpotSelect）へ遷移させる。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    isAchievedCouponSpotSpecificWithContext: ({ context }) => {
      if (context.userRewardStatus?.status === 'ACHIEVABLE') return false
      if (context.transaction?.prize?.type === 'COUPON' && context.transaction.prize.couponSpecificSpots) return true
      return (
        context.userRewardStatus?.status === 'ACHIEVED' &&
        (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'COUPON' && p.prize?.couponSpecificSpots)
      )
    },
    /**
     * 応募フォーム未提出が残っている場合。
     * 同一セッション内: context.applicationForm !== null で判定。
     * ページリロード後: prizes[] の最新当選エントリ（isLotteryLost=false の末尾）から判定。
     * ACHIEVED / UNAPPLIED 両ステータスを対象とする。
     */
    hasPendingApplicationForm: ({ context }) => {
      // status === 'ACHIEVABLE' の場合は新規獲得フローを優先するため false を返す
      if (context.userRewardStatus?.status === 'ACHIEVABLE') return false
      if (context.applicationForm !== null) return true
      const status = context.userRewardStatus?.status
      return (
        (status === 'ACHIEVED' || status === 'UNAPPLIED') &&
        [...(context.userRewardStatus?.prizes ?? [])].reverse().find((p) => !p.isLotteryLost)?.prize?.applicationForm !=
          null
      )
    },
    /**
     * ITEM を獲得済み（ACHIEVED）で再表示可能な場合。
     * 再POSTせず直接 itemConfirm に遷移させる。
     * ページリロード後は context.userRewardStatus.prizes から ITEM を検出。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    isAchievedItemWithContext: ({ context }) => {
      if (context.userRewardStatus?.status === 'ACHIEVABLE') return false
      if (context.transaction?.prize?.type === 'ITEM') return true
      return (
        context.userRewardStatus?.status === 'ACHIEVED' &&
        (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'ITEM')
      )
    },
    isPrizeDlc: ({ context }) => context.transaction?.prize?.type === 'DLC',
    isPrizeArPhoto: ({ context }) => context.transaction?.prize?.type === 'AR_PHOTO',
    isPrizeItem: ({ context }) => context.transaction?.prize?.type === 'ITEM',
    isPrizeCoupon: ({ context }) => context.transaction?.prize?.type === 'COUPON',
    isPrizeGiftCode: ({ context }) => context.transaction?.prize?.type === 'GIFT_CODE',
    isGiftee: ({ context }) => !!context.transaction?.gifteeBoxUrl,
    /**
     * context に GIFT_CODE トランザクションが残っている場合（ダイアログ再表示）。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    hasGiftCodeInTransaction: ({ context }) =>
      context.userRewardStatus?.status !== 'ACHIEVABLE' && context.transaction?.prize?.type === 'GIFT_CODE',
    /**
     * context に Giftee Box URL が残っている場合（ダイアログ再表示）。
     * ※ ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）。
     */
    hasGifteeUrlInTransaction: ({ context }) =>
      context.userRewardStatus?.status !== 'ACHIEVABLE' && !!context.transaction?.gifteeBoxUrl,
    /**
     * EXCHANGED + GIFT_CODE かつ APIレスポンスの prizes にコードが含まれる場合。
     * EXCHANGED 時は GET /api/v2/user/rewards/{id} の prizes[].code にコードが返るため、
     * POST（再取得）なしで giftCodeDisplay に遷移できる。
     */
    isExchangedGiftCodeWithStoredCode: ({ context }) =>
      context.userRewardStatus?.status === 'EXCHANGED' &&
      (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'GIFT_CODE' && p.prize?.code != null),
    /**
     * ACHIEVED 状態かつ prizes に GIFT_CODE が含まれる場合（ページリロード後）。
     * ACHIEVED では GET で code が返らないため POST もしない（重複トランザクション防止）。
     * isAchievableOrAchieved へのフォールスルーを防ぐ安全網として idle へ戻す。
     */
    isAchievedGiftCodeWithContext: ({ context }) =>
      context.userRewardStatus?.status === 'ACHIEVED' &&
      (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'GIFT_CODE'),
    /** ACHIEVED 状態かつ prizes に GIFTEE が含まれる場合（ページリロード後の再取得） */
    isAchievedGifteeWithContext: ({ context }) =>
      context.userRewardStatus?.status === 'ACHIEVED' &&
      (context.userRewardStatus?.prizes ?? []).some((p) => p.prize?.type === 'GIFTEE'),
    isCouponSpotSpecific: ({ context }) =>
      context.transaction?.prize?.type === 'COUPON' && !!context.transaction?.prize?.couponSpecificSpots,
    isApplicationForm: ({ context }) => context.applicationForm !== null,
    /**
     * 賞品選択方式が SINGLE（賞品確定）かつ獲得済み（ACHIEVED）の場合。
     * すでに acquire 済みのため再 acquire せず、直接 exchange に遷移する。
     */
    isSingleAndAchieved: ({ context }) =>
      context.userRewardStatus?.prizeSelectionType === 'SINGLE' && context.userRewardStatus?.status === 'ACHIEVED',
    /**
     * context.transaction.status が EXCHANGED の場合。
     * giftCodeDisplay / gifteeRedirect から EXCHANGE_CONFIRMED が発火しても
     * EXCHANGED 再表示の場合は exchanging（POST）に遷移しないようにするガード。
     */
    isTransactionExchanged: ({ context }) => context.transaction?.status === 'EXCHANGED',
    /**
     * 応募フォームが提出済み（APPLIED）の場合。
     * ボタン押下時に何もせず idle に留まる安全網。
     */
    isApplied: ({ context }) => context.userRewardStatus?.status === 'APPLIED',
    /**
     * スタンプ消費確認が必要な場合。
     * ACHIEVABLE 状態 かつ isStampConsumptionRequired=true かつ consumptionCount > 0 の
     * ときのみ確認ダイアログを表示する。
     * ACHIEVED 状態では消費済みのため表示しない。
     * consumptionCount = 0 の場合はダイアログを表示しない。
     */
    needsStampConsumptionConfirm: ({ context }) => {
      if (context.reward?.isStampConsumptionRequired !== true) return false
      if (context.userRewardStatus?.status !== 'ACHIEVABLE') return false
      const count =
        context.userRewardStatus.conditions.find((c) => c.type === 'STAMP_COUNT')?.threshold ??
        context.reward.stamps?.length ??
        0
      return count > 0
    },
    /**
     * DIALOG_EXCHANGE_PRESSED ガード群。
     * ベース実装は context.userRewardStatus.prizes を参照するが、
     * 初回ロードが未認証で実行された場合 prizes: [] になることがあるため、
     * .provide() でオーバーライドして freshUserRewardStatusRef.current を使用する。
     *
     * event を RewardFlowEvent 型でキャストしているのは、XState v5 の setup guards では
     * event が全イベントのユニオン型になるため。実際は DIALOG_EXCHANGE_PRESSED のみから呼ばれる。
     */
    isDialogPrizeApplicationForm: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      return findDialogPrize(context, e.transactionId)?.prize?.fulfillmentType === 'APPLICATION_FORM'
    },
    isDialogPrizeDlc: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      return findDialogPrize(context, e.transactionId)?.prize?.type === 'DLC'
    },
    isDialogPrizeArPhoto: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      return findDialogPrize(context, e.transactionId)?.prize?.type === 'AR_PHOTO'
    },
    isDialogPrizeCouponSpotSpecific: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      const prize = findDialogPrize(context, e.transactionId)?.prize
      return prize?.type === 'COUPON' && !!prize?.couponSpecificSpots
    },
    isDialogPrizeCoupon: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      return findDialogPrize(context, e.transactionId)?.prize?.type === 'COUPON'
    },
    isDialogPrizeItem: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      return findDialogPrize(context, e.transactionId)?.prize?.type === 'ITEM'
    },
    isDialogPrizeGiftCodeWithCode: ({ context, event }) => {
      const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
      const entry = findDialogPrize(context, e.transactionId)
      return entry?.prize?.type === 'GIFT_CODE' && entry.prize.code != null
    },
  },
  actions: {
    /**
     * acquiring 開始前に context.userRewardStatus を最新値に同期する。
     * 初回ロードが未認証で実行された場合 prizeSelectionType が null のまま残るため、
     * acquiring.onDone の LOTTERY ガードが誤って失敗するのを防ぐ。
     * 実装は useRewardFlowStateMachine の .provide() でオーバーライドする。
     */
    syncUserRewardStatus: assign({ userRewardStatus: ({ context }) => context.userRewardStatus }),
    /**
     * applicationForm state 入場時に applicationForm / applicationFormTransactionId を
     * 最新の context.userRewardStatus（syncUserRewardStatus で更新済み）から再設定する。
     * 初回ロードが未認証で実行された場合 context.userRewardStatus.prizes が [] のまま残り
     * context.applicationForm が null になる問題を防ぐ。
     * 実装は useRewardFlowStateMachine の .provide() でオーバーライドする。
     */
    syncApplicationFormFromStatus: assign({
      applicationForm: ({ context }) => context.applicationForm,
      applicationFormTransactionId: ({ context }) => context.applicationFormTransactionId,
    }),
  },
}).createMachine({
  id: 'rewardFlow',
  context: ({ input }) => ({
    rewardId: input.rewardId,
    isAuthenticated: input.isAuthenticated,
    reward: null,
    userRewardStatus: null,
    transaction: null,
    applicationForm: null,
    applicationFormTransactionId: null,
    selectedTransactionId: null,
    pendingFormData: null,
    pendingSpotId: null,
    couponRedeemDone: false,
    errorMessage: null,
    serverValidationErrors: null,
    completedVia: null,
    arPhotoFrames: [],
    arPhotoFramesReady: false,
  }),
  initial: 'initialLoading',
  states: {
    initialLoading: {
      invoke: {
        src: 'loadData',
        onDone: {
          target: 'idle',
          actions: assign({
            reward: ({ event }) => event.output.reward,
            userRewardStatus: ({ event }) => event.output.userRewardStatus,
            // ロード完了時に古いトランザクション情報と completedVia をリセット。
            // complete → reloading → initialLoading → idle の流れで残留した値が
            // 次回の EXCHANGE_CONFIRMED で意図せず POST を引き起こすのを防ぐ。
            transaction: () => null,
            completedVia: () => null,
            selectedTransactionId: () => null,
            couponRedeemDone: () => false,
            pendingSpotId: () => null,
          }),
        },
        onError: {
          target: 'idle',
        },
      },
    },
    idle: {
      on: {
        ACQUIRE_BUTTON_PRESSED: [
          {
            guard: 'isNotAuthenticated',
            target: 'loginRequired',
          },
          {
            guard: 'isApplied',
            target: 'idle',
          },
          {
            guard: 'isAchievedArPhotoWithContext',
            target: 'arNavigate',
            // context.userRewardStatus を freshUserRewardStatusRef で最新化してから
            // arNavigate の invoke.input を評価するため syncUserRewardStatus を適用する。
            // acquiring state entry と同じパターン。
            actions: 'syncUserRewardStatus',
          },
          {
            guard: 'isAchievedDlcWithContext',
            target: 'dlcAcquired',
            actions: assign({
              selectedTransactionId: ({ event }) => event.transactionId ?? null,
            }),
          },
          {
            guard: 'isAchievedCouponSpotSpecificWithContext',
            target: 'couponSpotSelect',
            actions: assign({
              selectedTransactionId: ({ event }) => event.transactionId ?? null,
            }),
          },
          {
            guard: 'isAchievedCouponWithContext',
            target: 'couponUse',
            actions: assign({
              selectedTransactionId: ({ event }) => event.transactionId ?? null,
            }),
          },
          {
            guard: 'hasPendingApplicationForm',
            target: 'applicationForm',
            actions: assign({
              completedVia: () => 'application' as const,
              applicationForm: ({ context }) => {
                if (context.applicationForm !== null) return context.applicationForm
                const latestWinning = [...(context.userRewardStatus?.prizes ?? [])]
                  .reverse()
                  .find((p) => !p.isLotteryLost)
                return latestWinning?.prize?.applicationForm ?? null
              },
              applicationFormTransactionId: ({ context }) => {
                if (context.applicationFormTransactionId !== null) return context.applicationFormTransactionId
                const latestWinning = [...(context.userRewardStatus?.prizes ?? [])]
                  .reverse()
                  .find((p) => !p.isLotteryLost)
                return latestWinning?.userRewardTransactionId ?? null
              },
            }),
          },
          {
            guard: 'isAchievedItemWithContext',
            target: 'itemConfirm',
            actions: assign({
              completedVia: () => 'exchange' as const,
              selectedTransactionId: ({ event }) => event.transactionId ?? null,
            }),
          },
          {
            guard: 'hasGiftCodeInTransaction',
            target: 'giftCodeDisplay',
          },
          {
            guard: 'hasGifteeUrlInTransaction',
            target: 'gifteeRedirect',
          },
          {
            // EXCHANGED + GIFT_CODE: APIレスポンスのコードから context.transaction を復元（POST不要）
            guard: 'isExchangedGiftCodeWithStoredCode',
            target: 'giftCodeDisplay',
            actions: assign({
              transaction: ({ context }) => {
                const entry = (context.userRewardStatus?.prizes ?? []).find(
                  (p) => p.prize?.type === 'GIFT_CODE' && p.prize?.code != null,
                )
                const prize = entry?.prize
                if (!prize?.code) return context.transaction
                return {
                  // guard (isExchangedGiftCodeWithStoredCode) の前提として userRewardStatus は非 null
                  // biome-ignore lint/style/noNonNullAssertion: guard により非 null が保証されている
                  id: context.userRewardStatus!.id,
                  transactionId: context.userRewardStatus?.transactionId ?? null,
                  status: 'EXCHANGED' as const,
                  createdAt: null,
                  updatedAt: null,
                  name: null,
                  fulfillmentType: prize.fulfillmentType,
                  inventoryType: null,
                  stock: null,
                  rewardGroup: null,
                  prize: {
                    id: prize.id,
                    name: prize.name,
                    type: 'GIFT_CODE' as const,
                    fulfillmentType: prize.fulfillmentType,
                    contentFile: prize.contentFile ?? null,
                    code: prize.code,
                    applicationForm: null,
                    couponSpots: [],
                  },
                  gifteeBoxUrl: null,
                }
              },
            }),
          },
          {
            // ACHIEVED + GIFT_CODE（ページリロード後）: POSTしない（重複トランザクション防止）
            // isAchievableOrAchieved へのフォールスルーを防ぐ安全網
            guard: 'isAchievedGiftCodeWithContext',
            target: 'idle',
          },
          {
            guard: 'isAchievedGifteeWithContext',
            target: 'acquiring',
          },
          {
            guard: 'isSingleAndAchieved',
            target: 'exchanging',
            actions: assign({
              completedVia: () => 'exchange' as const,
              selectedTransactionId: ({ event }) => event.transactionId ?? null,
            }),
          },
          {
            guard: 'needsStampConsumptionConfirm',
            target: 'stampConsumptionConfirm',
          },
          {
            guard: 'isAchievableOrAchieved',
            target: 'questionnaireCheck',
          },
        ],
        /**
         * 抽選結果一覧 / 獲得賞品一覧ダイアログ内のボタン押下。
         * prizes[] から transactionId に一致するエントリを探し、
         * prize.type / fulfillmentType に応じて直接 exchange フローへ遷移する。
         * POST /api/v2/rewards/{id}（acquiring）は経由しない。
         */
        DIALOG_EXCHANGE_PRESSED: [
          {
            // APPLICATION_FORM → applicationForm（応募フロー）
            guard: 'isDialogPrizeApplicationForm',
            target: 'applicationForm',
            actions: [
              // スタール context に備えて最初に userRewardStatus を最新化してから assign する
              'syncUserRewardStatus',
              assign({
                completedVia: () => 'application' as const,
                selectedTransactionId: ({ event }) => event.transactionId,
                applicationFormTransactionId: ({ context, event }) => {
                  const entry = findDialogPrize(context, event.transactionId)
                  // entry.userRewardTransactionId（PrizeHistoryData 直属のID）を優先する
                  return entry?.userRewardTransactionId ?? event.transactionId
                },
                applicationForm: ({ context, event }) =>
                  findDialogPrize(context, event.transactionId)?.prize?.applicationForm ?? null,
              }),
            ],
          },
          {
            // DLC → dlcAcquired（ダウンロードフロー）
            guard: 'isDialogPrizeDlc',
            target: 'dlcAcquired',
            actions: assign({ selectedTransactionId: ({ event }) => event.transactionId }),
          },
          {
            // AR_PHOTO → arNavigate（ARカメラ起動フロー）
            guard: 'isDialogPrizeArPhoto',
            target: 'arNavigate',
            actions: ['syncUserRewardStatus', assign({ selectedTransactionId: ({ event }) => event.transactionId })],
          },
          {
            // COUPON（スポット指定） → couponSpotSelect
            guard: 'isDialogPrizeCouponSpotSpecific',
            target: 'couponSpotSelect',
            actions: ['syncUserRewardStatus', assign({ selectedTransactionId: ({ event }) => event.transactionId })],
          },
          {
            // COUPON（通常） → couponUse
            guard: 'isDialogPrizeCoupon',
            target: 'couponUse',
            actions: ['syncUserRewardStatus', assign({ selectedTransactionId: ({ event }) => event.transactionId })],
          },
          {
            // ITEM → itemConfirm（受け取り確認フロー）
            guard: 'isDialogPrizeItem',
            target: 'itemConfirm',
            actions: assign({
              completedVia: () => 'exchange' as const,
              selectedTransactionId: ({ event }) => event.transactionId,
            }),
          },
          {
            // GIFT_CODE（コードあり） → giftCodeDisplay
            // ACHIEVED では prize.code は null のため実質到達しないが、EXCHANGED 再表示に備えて定義
            guard: 'isDialogPrizeGiftCodeWithCode',
            target: 'giftCodeDisplay',
            actions: [
              // スタール context に備えて最初に userRewardStatus を最新化してから assign する
              'syncUserRewardStatus',
              assign({
                completedVia: () => 'exchange' as const,
                selectedTransactionId: ({ event }) => event.transactionId,
                transaction: ({ context, event }) => {
                  const entry = findDialogPrize(context, event.transactionId)
                  const prize = entry?.prize
                  if (!prize?.code) return context.transaction
                  return {
                    // biome-ignore lint/style/noNonNullAssertion: findDialogPrize により非 null が保証
                    id: context.userRewardStatus!.id,
                    transactionId: event.transactionId,
                    // prizes[i].status が EXCHANGED のとき transaction.status も EXCHANGED にし、
                    // EXCHANGE_CONFIRMED で isTransactionExchanged ガードを通して POST を防ぐ
                    status: entry?.status ?? 'ACHIEVED',
                    createdAt: entry?.acquiredAt ?? null,
                    updatedAt: null,
                    name: null,
                    fulfillmentType: prize.fulfillmentType,
                    inventoryType: null,
                    stock: null,
                    rewardGroup: null,
                    prize: {
                      id: prize.id,
                      name: prize.name,
                      type: 'GIFT_CODE' as const,
                      fulfillmentType: prize.fulfillmentType,
                      contentFile: prize.contentFile ?? null,
                      code: prize.code,
                      applicationForm: null,
                      couponSpots: [],
                    },
                    gifteeBoxUrl: null,
                  }
                },
              }),
            ],
          },
          // GIFTEE: gifteeBoxUrl が PrizeHistoryData に含まれないため no-op（idle に留まる）
          // GIFT_CODE（code なし）: ACHIEVED では code が null のため no-op
          // 不明な type: no-op
          { target: 'idle' },
        ],
      },
    },
    stampConsumptionConfirm: {
      on: {
        STAMP_CONSUMPTION_CONFIRMED: { target: 'questionnaireCheck' },
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    questionnaireCheck: {
      on: {
        QUESTIONNAIRE_ANSWERED: { target: 'acquiring' },
        QUESTIONNAIRE_DISMISSED: { target: 'idle' },
      },
    },
    acquiring: {
      entry: 'syncUserRewardStatus',
      invoke: {
        src: 'acquireReward',
        input: ({ context }) => ({
          rewardId: context.rewardId,
          isLottery: context.userRewardStatus?.prizeSelectionType === 'LOTTERY',
        }),
        onDone: [
          {
            // event.output が null、または reward: null かつ isLotteryLost でもない = 取得失敗
            guard: ({ event }) => !event.output || (event.output.reward === null && !event.output.isLotteryLost),
            target: 'error',
            actions: assign({ errorMessage: () => '特典情報が取得できませんでした。' }),
          },
          {
            // LOTTERY ハズレ（新: status === 'LOST' or prize.isLotteryLost, 旧: ルート isLotteryLost）
            guard: ({ event, context }) =>
              context.userRewardStatus?.prizeSelectionType === 'LOTTERY' &&
              (event.output?.reward?.status === 'LOST' ||
                event.output?.reward?.prize?.isLotteryLost === true ||
                event.output?.isLotteryLost === true),
            target: 'lotteryLose',
            actions: assign({ transaction: ({ event }) => event.output?.reward ?? null }),
          },
          {
            // LOTTERY あたり（reward が非 null かつ LOST でなく isLotteryLost が falsy）
            guard: ({ event, context }) =>
              context.userRewardStatus?.prizeSelectionType === 'LOTTERY' &&
              event.output?.reward != null &&
              event.output?.reward?.status !== 'LOST' &&
              !event.output?.reward?.prize?.isLotteryLost,
            target: 'lotteryWin',
            actions: assign({
              transaction: ({ event }) => event.output?.reward ?? null,
              applicationForm: ({ event }) => event.output?.reward?.prize?.applicationForm ?? null,
              applicationFormTransactionId: ({ event }) => event.output?.reward?.prize?.userRewardTransactionId ?? null,
            }),
          },
          {
            guard: ({ event }) => !!event.output?.reward?.gifteeBoxUrl,
            target: 'gifteeRedirect',
            actions: assign({ transaction: ({ event }) => event.output?.reward ?? null }),
          },
          {
            guard: ({ event }) => event.output?.reward?.prize?.type === 'DLC',
            target: 'dlcAcquired',
            actions: assign({ transaction: ({ event }) => event.output?.reward ?? null }),
          },
          {
            guard: ({ event }) => event.output?.reward?.prize?.type === 'AR_PHOTO',
            target: 'arPhotoAcquired',
            actions: assign({ transaction: ({ event }) => event.output?.reward ?? null }),
          },
          {
            // prize に applicationForm が含まれる場合（fulfillmentType に関わらず）
            guard: ({ event }) => event.output?.reward?.prize?.applicationForm != null,
            target: 'applicationForm',
            actions: assign({
              transaction: ({ event }) => event.output?.reward ?? null,
              applicationForm: ({ event }) => event.output?.reward?.prize?.applicationForm ?? null,
              applicationFormTransactionId: ({ event }) => event.output?.reward?.prize?.userRewardTransactionId ?? null,
              completedVia: () => 'application' as const,
            }),
          },
          {
            guard: ({ event }) => event.output?.reward?.prize?.type === 'ITEM',
            target: 'itemConfirm',
            actions: assign({
              transaction: ({ event }) => event.output?.reward ?? null,
              completedVia: () => 'exchange' as const,
            }),
          },
          {
            guard: ({ event }) =>
              event.output?.reward?.prize?.type === 'COUPON' && !!event.output?.reward?.prize?.couponSpecificSpots,
            target: 'couponSpotSelect',
            actions: assign({
              transaction: ({ event }) => event.output?.reward ?? null,
              completedVia: () => 'exchange' as const,
            }),
          },
          {
            guard: ({ event }) => event.output?.reward?.prize?.type === 'COUPON',
            target: 'couponUse',
            actions: assign({
              transaction: ({ event }) => event.output?.reward ?? null,
              completedVia: () => 'exchange' as const,
            }),
          },
          {
            guard: ({ event }) => event.output?.reward?.prize?.type === 'GIFT_CODE',
            target: 'giftCodeDisplay',
            actions: assign({
              transaction: ({ event }) => event.output?.reward ?? null,
              completedVia: () => 'exchange' as const,
            }),
          },
          {
            target: 'error',
            actions: assign({ errorMessage: () => '未対応の特典タイプです。' }),
          },
        ],
        onError: {
          target: 'error',
          actions: assign({
            errorMessage: ({ event }) => resolveExchangeErrorMessage(event.error),
          }),
        },
      },
    },
    dlcAcquired: {
      on: {
        DLC_DOWNLOAD: { target: 'exchanging' },
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    arPhotoAcquired: {
      on: {
        AR_CAMERA_START: { target: 'arNavigate' },
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    arNavigate: {
      exit: assign({ arPhotoFrames: () => [], arPhotoFramesReady: () => false }),
      invoke: [
        {
          src: 'fetchRewardPhotoFrames',
          input: ({ context }) => ({
            // transactionId の解決（優先順）:
            // 1. 同一セッション内の獲得直後: context.transaction.transactionId
            //    （UserRewardTransaction.transactionId = user_reward_transactions.id。exchange と同じフィールド）
            //    ※ context.transaction.id はレガシー（reward.id が入る場合あり）のため使用しない
            //    ※ context.transaction.prize.userRewardTransactionId は「応募フォーム用」のため AR_PHOTO では null
            // 2. ダイアログ経由の選択: context.selectedTransactionId（DIALOG_EXCHANGE_PRESSED で設定）
            // 3. ページリロード後: context.userRewardStatus.prizes から当選 AR_PHOTO エントリを検索
            // 4. prizes が空の場合: reward.transactionId（user_reward_transactions.id）を直接使用
            //    （GET /api/v2/user/rewards/{id} の reward.transactionId = 最新トランザクション ID）
            transactionId:
              context.transaction?.transactionId ??
              context.selectedTransactionId ??
              (context.userRewardStatus?.prizes ?? [])
                .filter((p) => p.prize?.type === 'AR_PHOTO' && !p.isLotteryLost)
                .at(-1)?.userRewardTransactionId ??
              context.userRewardStatus?.transactionId ??
              null,
          }),
          onDone: {
            actions: assign({ arPhotoFrames: ({ event }) => event.output, arPhotoFramesReady: () => true }),
          },
          onError: {
            // API エラー時はフレームなし（空配列のまま）でカメラを起動する。
            // try/catch は actor 内ではなく XState onError に集約することで、
            // 想定外例外も state machine 側で握れる。
            actions: assign({ arPhotoFramesReady: () => true }),
          },
        },
        {
          // ARカメラ起動時に exchange を実行する。
          // fetchRewardPhotoFrames と並行実行し、失敗してもARカメラ起動を妨げない。
          src: 'exchangeArPhoto',
          input: ({ context }) => ({
            transactionId:
              context.transaction?.transactionId ??
              context.selectedTransactionId ??
              (context.userRewardStatus?.prizes ?? [])
                .filter((p) => p.prize?.type === 'AR_PHOTO' && !p.isLotteryLost)
                .at(-1)?.userRewardTransactionId ??
              context.userRewardStatus?.transactionId ??
              null,
          }),
          onError: {
            // exchange 失敗はARカメラ起動を妨げない（エラーはログに記録済み）
          },
        },
      ],
      on: {
        AR_OVERLAY_CLOSED: { target: 'idle' },
      },
    },
    itemConfirm: {
      on: {
        ITEM_CONFIRM: { target: 'exchanging' },
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    applicationForm: {
      // 遷移時の inline assign は context.userRewardStatus（初回ロードが未認証の場合は prizes=[]）
      // を参照するため applicationForm が null になる場合がある。
      // entry でまず syncUserRewardStatus で最新化し、続けて syncApplicationFormFromStatus で再設定する。
      entry: ['syncUserRewardStatus', 'syncApplicationFormFromStatus'],
      on: {
        APPLICATION_FORM_SUBMIT: {
          target: 'applicationFormConfirm',
          actions: assign({
            pendingFormData: ({ event }) => event.formData,
            serverValidationErrors: () => null,
          }),
        },
        DIALOG_CLOSED: {
          target: 'idle',
          // フォームを閉じる際にサーバーエラーをクリアし、次回表示時に古いエラーが残らないようにする
          actions: assign({ serverValidationErrors: () => null }),
        },
      },
    },
    applicationFormConfirm: {
      on: {
        APPLICATION_FORM_CONFIRM: { target: 'submittingApplicationForm' },
        DIALOG_CLOSED: { target: 'applicationForm' },
      },
    },
    submittingApplicationForm: {
      invoke: {
        src: 'submitApplicationForm',
        input: ({ context }) => ({
          transactionId: context.applicationFormTransactionId,
          formData: context.pendingFormData,
        }),
        onDone: {
          target: 'complete',
          actions: assign({
            applicationForm: () => null,
            applicationFormTransactionId: () => null,
            pendingFormData: () => null,
            serverValidationErrors: () => null,
          }),
        },
        onError: [
          {
            // 422 バリデーションエラー かつ フィールドエラーが取得できた場合のみ → 入力フォームに戻る
            // フィールドエラーが空（errors: {} など）の場合は error ステートへ落とし、何も表示されない状態を防ぐ
            guard: ({ event }) =>
              event.error instanceof ApiError &&
              (event.error as ApiError).status === 422 &&
              extractValidationErrors(event.error) !== null,
            target: 'applicationForm',
            actions: assign({
              serverValidationErrors: ({ event }) => extractValidationErrors(event.error),
            }),
          },
          {
            // それ以外のエラー（422 以外、または 422 でもフィールドエラーが取れない場合）→ error ステートへ
            target: 'error',
            actions: assign({
              errorMessage: ({ event }) => resolveApplicationFormErrorMessage(event.error),
              serverValidationErrors: () => null,
            }),
          },
        ],
      },
    },
    couponSpotSelect: {
      on: {
        // QR読み取り成功 → spotId をセットして couponUse へ
        COUPON_SPOT_SELECTED: {
          target: 'couponUse',
          actions: assign({ pendingSpotId: ({ event }) => event.spotId }),
        },
        // 「QRコードを読み込めない方はこちら」押下 → スポット選択ダイアログへ
        MANUAL_SPOT_SELECT_OPENED: { target: 'couponSpotSelectManual' },
        DIALOG_CLOSED: { target: 'idle', actions: assign({ pendingSpotId: () => null }) },
      },
    },
    couponSpotSelectManual: {
      on: {
        COUPON_SPOT_SELECTED: {
          target: 'couponUse',
          actions: assign({ pendingSpotId: ({ event }) => event.spotId }),
        },
        // ダイアログを閉じると QR読み取り画面に戻る
        DIALOG_CLOSED: { target: 'couponSpotSelect' },
      },
    },
    couponUse: {
      on: {
        COUPON_EXCHANGE: [
          {
            // スポット指定クーポン: couponRedeem 未完了 → 消込先行のため couponRedeeming へ
            guard: ({ context }) => !context.couponRedeemDone && resolveCouponConfigId(context) != null,
            target: 'couponRedeeming',
          },
          // 通常クーポン、または couponRedeem 完了済みのリトライ → exchanging へ
          { target: 'exchanging' },
        ],
        DIALOG_CLOSED: { target: 'idle', actions: assign({ pendingSpotId: () => null }) },
      },
    },
    couponRedeeming: {
      invoke: {
        src: 'redeemCoupon',
        input: ({ context }) => ({
          couponConfigId: resolveCouponConfigId(context)!,
          spotId: context.pendingSpotId,
        }),
        onDone: {
          target: 'exchanging',
          actions: assign({ couponRedeemDone: () => true }),
        },
        onError: {
          target: 'error',
          actions: assign({
            errorMessage: ({ event }) => resolveExchangeErrorMessage(event.error),
          }),
        },
      },
    },
    giftCodeDisplay: {
      on: {
        EXCHANGE_CONFIRMED: [
          {
            // フロー3: EXCHANGED 再表示 → POST しない → idle
            guard: 'isTransactionExchanged',
            target: 'idle',
          },
          {
            // フロー1: acquiring 直後（completedVia が設定されている）→ exchanging（POST）
            guard: ({ context }) => context.completedVia !== null,
            target: 'exchanging',
          },
          {
            // フロー2: DIALOG_CLOSED 後の再表示（completedVia がリセット済み）→ POST しない → idle
            target: 'idle',
          },
        ],
        DIALOG_CLOSED: {
          target: 'idle',
          // フロー2 判定用: DIALOG_CLOSED で completedVia をリセット
          // 次回 hasGiftCodeInTransaction で再表示した際に EXCHANGE_CONFIRMED が POST しないようにする
          actions: assign({ completedVia: () => null }),
        },
      },
    },
    gifteeRedirect: {
      on: {
        EXCHANGE_CONFIRMED: [
          {
            // EXCHANGED 再表示の場合は exchange（POST）しない → idle に戻る
            guard: 'isTransactionExchanged',
            target: 'idle',
          },
          { target: 'exchanging' },
        ],
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    exchanging: {
      invoke: {
        src: 'exchangeReward',
        input: ({ context }) => ({
          spotId: context.pendingSpotId,
          // context.transaction.transactionId → prize.userRewardTransactionId → selectedTransactionId の順で優先
          // (DLC等で acquire 直後に exchange する場合は transaction が設定されている)
          // transactionId が null の場合は prize.userRewardTransactionId にフォールバック（応募フォームと同じ解決方式）
          transactionId:
            context.transaction?.transactionId ??
            context.transaction?.prize?.userRewardTransactionId ??
            context.selectedTransactionId ??
            null,
        }),
        onDone: { target: 'complete' },
        onError: {
          target: 'error',
          actions: assign({
            errorMessage: ({ event }) => resolveExchangeErrorMessage(event.error),
          }),
        },
      },
    },
    complete: {
      on: {
        DIALOG_CLOSED: { target: 'reloading' },
      },
    },
    reloading: {
      always: { target: 'initialLoading' },
    },
    error: {
      on: {
        RETRY: { target: 'acquiring' },
        RETRY_APPLICATION_FORM: { target: 'submittingApplicationForm' },
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    loginRequired: {
      on: {
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    lotteryWin: {
      on: {
        LOTTERY_RECEIVE: { target: 'lotteryReceiving' },
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
    lotteryReceiving: {
      always: [
        { guard: 'isPrizeDlc', target: 'dlcAcquired' },
        { guard: 'isPrizeArPhoto', target: 'arPhotoAcquired' },
        {
          guard: 'isApplicationForm',
          target: 'applicationForm',
          actions: assign({ completedVia: () => 'application' as const }),
        },
        {
          guard: 'isPrizeItem',
          target: 'itemConfirm',
          actions: assign({ completedVia: () => 'exchange' as const }),
        },
        {
          guard: 'isCouponSpotSpecific',
          target: 'couponSpotSelect',
          actions: assign({ completedVia: () => 'exchange' as const }),
        },
        {
          guard: 'isPrizeCoupon',
          target: 'couponUse',
          actions: assign({ completedVia: () => 'exchange' as const }),
        },
        {
          guard: 'isPrizeGiftCode',
          target: 'giftCodeDisplay',
          actions: assign({ completedVia: () => 'exchange' as const }),
        },
        {
          guard: 'isGiftee',
          target: 'gifteeRedirect',
          actions: assign({ completedVia: () => 'exchange' as const }),
        },
        { target: 'idle' },
      ],
    },
    lotteryLose: {
      on: {
        DIALOG_CLOSED: { target: 'idle' },
      },
    },
  },
})

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRewardFlowStateMachine({
  rewardId,
  isAuthenticated,
  enabled,
}: {
  rewardId: number
  isAuthenticated: boolean
  enabled: boolean
}) {
  const queryClient = useQueryClient()
  const rewardDetailFetchFn = useTanstackRewardDetailFetchFn(rewardId)
  const userRewardDetailFetchFn = useTanstackUserRewardDetailFetchFn(rewardId)
  const questionnairesTypeFetchFn = useTanstackQuestionnairesTypeFetchFn('reward')
  const apiRef = useApiRef()

  // hydration 直後や campaignSlug 変化による machine 再起動で isAuthenticated が一時的に
  // false → true と変化する場合、その都度 machine が再作成されると不要な initialLoading が
  // 発生しコードが表示されない問題が起きる。
  // 一度 true になったらページのライフサイクル中は false に戻さない sticky な値を使うことで
  // machine の不必要な再作成を防ぐ。ログアウト時はページ全体がアンマウントされるため問題ない。
  const stableIsAuthRef = useRef(isAuthenticated)
  if (isAuthenticated) stableIsAuthRef.current = true
  const stableIsAuthenticated = stableIsAuthRef.current

  // リアクティブなクエリ購読: invalidateQueries に反応して自動再フェッチ
  const { data: rewardDetailData } = useQuery({ ...rewardDetailFetchFn, enabled })
  const { data: userRewardDetailData, isLoading: isUserRewardDetailLoading } = useQuery({
    ...userRewardDetailFetchFn,
    enabled: enabled && stableIsAuthenticated,
  })

  // Refs to capture latest values and prevent stale closures in actors
  // stableIsAuthenticated はリロード直後に false → true と変化するため ref で最新値を保持する
  // （useMachine はアクターを初回マウント時の1回のみ生成するため、クロージャの値が古くなる）
  const isAuthenticatedRef = useRef(stableIsAuthenticated)
  // freshUserRewardStatus も同様に ref で保持する
  // loadData が認証前に実行された場合 context.userRewardStatus.status は UNOBTAINED のままだが、
  // TanStack Query がリアクティブに再取得すると freshUserRewardStatus が ACHIEVABLE に更新される。
  // ガードがこの ref を参照することで、常に最新ステータスで判定できる。
  const freshUserRewardStatusRef = useRef<UserRewardStatusDetailData | null>(null)
  const freshRewardRef = useRef<MergedRewardData | null>(null)
  const queryClientRef = useRef(queryClient)
  const rewardDetailFetchFnRef = useRef(rewardDetailFetchFn)
  const userRewardDetailFetchFnRef = useRef(userRewardDetailFetchFn)
  const questionnairesTypeFetchFnRef = useRef(questionnairesTypeFetchFn)
  isAuthenticatedRef.current = stableIsAuthenticated
  queryClientRef.current = queryClient
  rewardDetailFetchFnRef.current = rewardDetailFetchFn
  userRewardDetailFetchFnRef.current = userRewardDetailFetchFn
  questionnairesTypeFetchFnRef.current = questionnairesTypeFetchFn

  // biome-ignore lint/correctness/useExhaustiveDependencies: actors use refs to access latest values; only recreate machine when rewardId/isAuthenticated/enabled changes
  const machine = useMemo(
    () =>
      rewardFlowMachineBase.provide({
        actors: {
          loadData: fromPromise(async () => {
            if (!enabled)
              return {
                reward: null as MergedRewardData | null,
                userRewardStatus: null as UserRewardStatusDetailData | null,
              }

            const resolveStatus = (
              reward: MergedRewardData | null,
              userRewardDetail: UserRewardStatusGetResponse | null,
            ): UserRewardStatusDetailData | null =>
              reward
                ? {
                    id: reward.id,
                    transactionId: userRewardDetail?.reward?.transactionId ?? null,
                    name: reward.name,
                    image: reward.image,
                    status: userRewardDetail?.reward?.status ?? reward.acquisitionStatus ?? 'UNOBTAINED',
                    conditions: reward.conditions,
                    createdAt: null,
                    prizes: userRewardDetail?.reward?.prizes ?? [],
                    prizeSelectionType: userRewardDetail?.reward?.prizeSelectionType ?? null,
                    isRepeatable: userRewardDetail?.reward?.isRepeatable ?? false,
                  }
                : null

            const [guestResult, authResult] = await Promise.all([
              // fetchRewardDetailOrInvalidate: 30秒以内の再アクセスはキャッシュ再利用、
              // 30秒超えは常にネットワークフェッチして期間外の 404 を正しく検知する（Issue #2866）。
              fetchRewardDetailOrInvalidate(
                queryClientRef.current,
                rewardDetailFetchFnRef.current.queryKey,
                rewardDetailFetchFnRef.current.queryFn,
              ),
              isAuthenticatedRef.current
                ? queryClientRef.current.fetchQuery(userRewardDetailFetchFnRef.current).catch((e: unknown) => {
                    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null
                    throw e
                  })
                : Promise.resolve(null),
              // アンケートをプリフェッチしてキャッシュに格納（questionnaireCheck 遷移後に即表示するため）。
              // 404（アンケートなし）の場合のみ setQueryData で null をキャッシュに書き込む（success 扱い）。
              // これにより useQuery 側は staleTime 内では再フェッチせず「1回のみ」要件を満たす。
              // 5xx 等のサーバーエラーは null を書かずエラーのままにし、
              // コンシューマーの retry ロジックが機能するようにする。
              isAuthenticatedRef.current
                ? queryClientRef.current
                    .fetchQuery({ ...questionnairesTypeFetchFnRef.current, retry: false })
                    .catch((e: unknown) => {
                      if (e instanceof ApiError && e.status === 404) {
                        queryClientRef.current.setQueryData(questionnairesTypeFetchFnRef.current.queryKey, null)
                      }
                      return null
                    })
                : Promise.resolve(null),
            ])

            if (!guestResult) return { reward: null, userRewardStatus: null }

            const result = mergeRewardDetail(guestResult, authResult ?? null)
            return {
              reward: result.reward,
              userRewardStatus: resolveStatus(result.reward, authResult ?? null),
            }
          }),
          acquireReward: fromPromise(async ({ input }: { input: AcquireInput }) => {
            // LOTTERY型の場合、APIと最小待機タイマーを並行実行し、両方完了後に遷移する
            // これによりGIFが確実に LOTTERY_GIF_MIN_DISPLAY_MS ミリ秒間表示される
            const LOTTERY_GIF_MIN_DISPLAY_MS = 4000
            const minDelay = input.isLottery
              ? new Promise<void>((resolve) => setTimeout(resolve, LOTTERY_GIF_MIN_DISPLAY_MS))
              : Promise.resolve()
            const [res] = await Promise.all([apiRef.current.auth.postReward(input.rewardId, {}), minDelay])
            if (!res) return null
            const parsed = UserRewardTransactionGetResponseSchema.safeParse(res)
            // 獲得後はユーザー獲得状況（userRewards）を無効化して次回取得時に最新データを反映する
            void queryClientRef.current.invalidateQueries({ queryKey: ['userRewards'] })
            if (!parsed.success) return null
            const data = parsed.data

            // APPLICATION_FORM 型の賞品がある場合、GET endpoint から prefill 付き applicationForm を取得して上書き
            if (data.reward?.prize?.applicationForm != null) {
              try {
                // キャッシュを削除して強制的に再取得（prefill は GET のみで取得可能）
                queryClientRef.current.removeQueries({
                  queryKey: userRewardDetailFetchFnRef.current.queryKey,
                  exact: true,
                })
                const freshStatus = await queryClientRef.current.fetchQuery(userRewardDetailFetchFnRef.current)
                const latestPrize = [...(freshStatus?.reward?.prizes ?? [])].reverse().find((p) => !p.isLotteryLost)
                const freshApplicationForm = latestPrize?.prize?.applicationForm ?? null
                if (freshApplicationForm != null && data.reward.prize != null) {
                  return {
                    ...data,
                    reward: {
                      ...data.reward,
                      prize: {
                        ...data.reward.prize,
                        applicationForm: freshApplicationForm,
                      },
                    },
                  }
                }
              } catch {
                // GET 失敗は無視: acquire 自体は成功しているため prefill なしで継続
              }
            }

            return data
          }),
          redeemCoupon: fromPromise(async ({ input }: { input: RedeemCouponInput }) => {
            await apiRef.current.auth.postCouponRedeem(input.couponConfigId, input.spotId ?? undefined)
          }),
          exchangeReward: fromPromise(async ({ input }: { input: ExchangeInput }) => {
            // transactionId を解決（context 直接 → キャッシュ → fetch の順で優先）
            let transactionId = input.transactionId

            if (!transactionId) {
              const cached = queryClientRef.current.getQueryData<UserRewardStatusGetResponse>(
                userRewardDetailFetchFnRef.current.queryKey,
              )
              transactionId = cached?.reward?.transactionId ?? null
            }

            if (!transactionId) {
              const fetched = await queryClientRef.current.fetchQuery(userRewardDetailFetchFnRef.current)
              transactionId = fetched?.reward?.transactionId ?? null
            }

            if (!transactionId) {
              throw new Error('特典情報を取得できませんでした。もう一度特典を獲得してください。')
            }

            // スポット指定クーポン消込: postCouponRedeem は couponRedeeming 状態で先行実行済み
            const response = await apiRef.current.auth.postUserRewardTransaction(transactionId, {
              ...(input.spotId != null ? { spotId: input.spotId } : {}),
            })
            if (!response) throw new Error('特典の交換に失敗しました')
            const parsed = UserRewardTransactionGetResponseSchema.safeParse(response)
            if (!parsed.success) throw new Error('特典の交換に失敗しました')
            // 交換後はユーザー獲得状況（userRewards）を無効化して次回取得時に最新データを反映する
            void queryClientRef.current.invalidateQueries({ queryKey: ['userRewards'] })
          }),
          submitApplicationForm: fromPromise(async ({ input }: { input: SubmitApplicationFormInput }) => {
            if (!input.transactionId) {
              throw new Error('応募フォームの送信に必要な情報が見つかりませんでした。')
            }
            await apiRef.current.auth.postRewardApplicationForm(input.transactionId, {
              fields: Object.entries(input.formData ?? {}).map(([fieldKey, value]) => ({ fieldKey, value })),
            })
            void queryClientRef.current.invalidateQueries({ queryKey: ['userRewards'] })
          }),
          fetchRewardPhotoFrames: fromPromise(async ({ input }: { input: FetchPhotoFramesInput }) => {
            // transactionId を解決（context 直接 → キャッシュ → fetch の順で優先）
            // exchangeReward と同じフォールバックパターンで、prizes が空でも
            // reward.transactionId から user_reward_transactions.id を解決する
            let transactionId = input.transactionId

            if (!transactionId) {
              const cached = queryClientRef.current.getQueryData<UserRewardStatusGetResponse>(
                userRewardDetailFetchFnRef.current.queryKey,
              )
              transactionId = cached?.reward?.transactionId ?? null
            }

            if (!transactionId) {
              const fetched = await queryClientRef.current.fetchQuery(userRewardDetailFetchFnRef.current)
              transactionId = fetched?.reward?.transactionId ?? null
            }

            if (transactionId == null) return []
            const res = await apiRef.current.auth.postRewardTransactionPhotoFrames(transactionId)
            return toPhotoFrameItems(res.photoFrames)
          }),
          exchangeArPhoto: fromPromise(async ({ input }: { input: FetchPhotoFramesInput }) => {
            // transactionId を解決（context 直接 → キャッシュ → fetch の順で優先）
            // fetchRewardPhotoFrames と同じフォールバックパターン
            let transactionId = input.transactionId

            if (!transactionId) {
              const cached = queryClientRef.current.getQueryData<UserRewardStatusGetResponse>(
                userRewardDetailFetchFnRef.current.queryKey,
              )
              transactionId = cached?.reward?.transactionId ?? null
            }

            if (!transactionId) {
              const fetched = await queryClientRef.current.fetchQuery(userRewardDetailFetchFnRef.current)
              transactionId = fetched?.reward?.transactionId ?? null
            }

            if (!transactionId) {
              // transactionId が解決できない場合はエラーをスローするが、
              // arNavigate の onError で握りつぶされる（ARカメラ起動は妨げない）
              throw new Error('AR exchange: transactionId を解決できませんでした')
            }

            await apiRef.current.auth.postUserRewardTransaction(transactionId)
            void queryClientRef.current.invalidateQueries({ queryKey: ['userRewards'] })
          }),
        },
        // isAuthenticated はリロード直後に false→true と変化するためガードを ref ベースにオーバーライドする
        // context.isAuthenticated は useMachine が初回マウント時にのみ作成されるため古い値を参照してしまう
        // isAchievableOrAchieved / isAchievedArPhotoWithContext / isAchievedCouponWithContext も同様に
        // freshUserRewardStatusRef を参照することでリロード後の staleness 問題を防ぐ
        guards: {
          isNotAuthenticated: () => !isAuthenticatedRef.current,
          isAchievableOrAchieved: () =>
            freshUserRewardStatusRef.current?.status === 'ACHIEVABLE' ||
            (freshUserRewardStatusRef.current?.status === 'ACHIEVED' &&
              freshUserRewardStatusRef.current?.prizeSelectionType !== 'LOTTERY'),
          isAchievedArPhotoWithContext: ({ context }) => {
            if (freshUserRewardStatusRef.current?.status === 'ACHIEVABLE') return false
            if (context.transaction?.prize?.type === 'AR_PHOTO') return true
            const s = freshUserRewardStatusRef.current
            return (
              (s?.status === 'ACHIEVED' || s?.status === 'EXCHANGED') &&
              (s?.prizes ?? []).some((p) => p.prize?.type === 'AR_PHOTO')
            )
          },
          isAchievedDlcWithContext: ({ context }) => {
            if (freshUserRewardStatusRef.current?.status === 'ACHIEVABLE') return false
            if (context.transaction?.prize?.type === 'DLC') return true
            const s = freshUserRewardStatusRef.current
            return (
              (s?.status === 'ACHIEVED' || s?.status === 'EXCHANGED') &&
              (s?.prizes ?? []).some((p) => p.prize?.type === 'DLC')
            )
          },
          isAchievedCouponWithContext: ({ context }) => {
            if (freshUserRewardStatusRef.current?.status === 'ACHIEVABLE') return false
            if (context.transaction?.prize?.type === 'COUPON' && !context.transaction.prize.couponSpecificSpots)
              return true
            const s = freshUserRewardStatusRef.current
            return (
              s?.status === 'ACHIEVED' &&
              (s?.prizes ?? []).some((p) => p.prize?.type === 'COUPON' && !p.prize?.couponSpecificSpots)
            )
          },
          isAchievedCouponSpotSpecificWithContext: ({ context }) => {
            if (freshUserRewardStatusRef.current?.status === 'ACHIEVABLE') return false
            if (context.transaction?.prize?.type === 'COUPON' && context.transaction.prize.couponSpecificSpots)
              return true
            const s = freshUserRewardStatusRef.current
            return (
              s?.status === 'ACHIEVED' &&
              (s?.prizes ?? []).some((p) => p.prize?.type === 'COUPON' && p.prize?.couponSpecificSpots)
            )
          },
          hasPendingApplicationForm: ({ context }) => {
            // status === 'ACHIEVABLE' の場合は新規獲得フローを優先するため false を返す
            if (freshUserRewardStatusRef.current?.status === 'ACHIEVABLE') return false
            if (context.applicationForm !== null) return true
            const s = freshUserRewardStatusRef.current
            const status = s?.status
            if (status !== 'ACHIEVED' && status !== 'UNAPPLIED') return false
            const latestWinning = [...(s?.prizes ?? [])].reverse().find((p) => !p.isLotteryLost)
            return (latestWinning?.prize?.applicationForm ?? null) !== null
          },
          isAchievedItemWithContext: ({ context }) => {
            if (freshUserRewardStatusRef.current?.status === 'ACHIEVABLE') return false
            if (context.transaction?.prize?.type === 'ITEM') return true
            const s = freshUserRewardStatusRef.current
            return s?.status === 'ACHIEVED' && (s?.prizes ?? []).some((p) => p.prize?.type === 'ITEM')
          },
          // ACHIEVABLE の場合は新規獲得フローを優先するため false を返す（isRepeatable=true 連続獲得対応）
          hasGiftCodeInTransaction: ({ context }) =>
            freshUserRewardStatusRef.current?.status !== 'ACHIEVABLE' &&
            context.transaction?.prize?.type === 'GIFT_CODE',
          hasGifteeUrlInTransaction: ({ context }) =>
            freshUserRewardStatusRef.current?.status !== 'ACHIEVABLE' && !!context.transaction?.gifteeBoxUrl,
          isSingleAndAchieved: () =>
            freshUserRewardStatusRef.current?.prizeSelectionType === 'SINGLE' &&
            freshUserRewardStatusRef.current?.status === 'ACHIEVED',
          isExchangedGiftCodeWithStoredCode: () => {
            const s = freshUserRewardStatusRef.current
            return (
              s?.status === 'EXCHANGED' &&
              (s?.prizes ?? []).some((p) => p.prize?.type === 'GIFT_CODE' && p.prize?.code != null)
            )
          },
          isAchievedGiftCodeWithContext: () => {
            const s = freshUserRewardStatusRef.current
            return s?.status === 'ACHIEVED' && (s?.prizes ?? []).some((p) => p.prize?.type === 'GIFT_CODE')
          },
          isApplied: () => freshUserRewardStatusRef.current?.status === 'APPLIED',
          needsStampConsumptionConfirm: () => {
            const reward = freshRewardRef.current
            const status = freshUserRewardStatusRef.current
            if (reward?.isStampConsumptionRequired !== true) return false
            if (status?.status !== 'ACHIEVABLE') return false
            const count =
              status.conditions.find((c) => c.type === 'STAMP_COUNT')?.threshold ?? reward.stamps?.length ?? 0
            return count > 0
          },
          // DIALOG_EXCHANGE_PRESSED ガード: freshUserRewardStatusRef を使用してスタール問題を回避
          isDialogPrizeApplicationForm: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            return (
              prizes.find((p) => p.userRewardTransactionId === e.transactionId)?.prize?.fulfillmentType ===
              'APPLICATION_FORM'
            )
          },
          isDialogPrizeDlc: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            return prizes.find((p) => p.userRewardTransactionId === e.transactionId)?.prize?.type === 'DLC'
          },
          isDialogPrizeArPhoto: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            return prizes.find((p) => p.userRewardTransactionId === e.transactionId)?.prize?.type === 'AR_PHOTO'
          },
          isDialogPrizeCouponSpotSpecific: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            const prize = prizes.find((p) => p.userRewardTransactionId === e.transactionId)?.prize
            return prize?.type === 'COUPON' && !!prize?.couponSpecificSpots
          },
          isDialogPrizeCoupon: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            return prizes.find((p) => p.userRewardTransactionId === e.transactionId)?.prize?.type === 'COUPON'
          },
          isDialogPrizeItem: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            return prizes.find((p) => p.userRewardTransactionId === e.transactionId)?.prize?.type === 'ITEM'
          },
          isDialogPrizeGiftCodeWithCode: ({ event }) => {
            const e = event as Extract<RewardFlowEvent, { type: 'DIALOG_EXCHANGE_PRESSED' }>
            const prizes = freshUserRewardStatusRef.current?.prizes ?? []
            const entry = prizes.find((p) => p.userRewardTransactionId === e.transactionId)
            return entry?.prize?.type === 'GIFT_CODE' && entry.prize.code != null
          },
        },
        actions: {
          // acquiring エントリ時に context.userRewardStatus を freshUserRewardStatusRef で上書きする。
          // 初回ロードが未認証で実行された場合 prizeSelectionType が null になり、
          // acquiring.onDone の LOTTERY ガードが誤って失敗する問題を防ぐ。
          syncUserRewardStatus: assign({
            userRewardStatus: ({ context }) => freshUserRewardStatusRef.current ?? context.userRewardStatus,
          }),
          // applicationForm state 入場時に applicationForm / applicationFormTransactionId を
          // freshUserRewardStatusRef（syncUserRewardStatus で更新済み context）から再設定する。
          // 初回ロードが未認証で実行された場合 context.userRewardStatus.prizes が [] のまま残り
          // 遷移時の inline assign で context.applicationForm が null になる問題を修正する。
          syncApplicationFormFromStatus: assign({
            applicationForm: ({ context }) => {
              if (context.applicationForm !== null) return context.applicationForm
              const latestWinning = [...(context.userRewardStatus?.prizes ?? [])]
                .reverse()
                .find((p) => !p.isLotteryLost)
              return latestWinning?.prize?.applicationForm ?? null
            },
            applicationFormTransactionId: ({ context }) => {
              if (context.applicationFormTransactionId !== null) return context.applicationFormTransactionId
              const latestWinning = [...(context.userRewardStatus?.prizes ?? [])]
                .reverse()
                .find((p) => !p.isLotteryLost)
              return latestWinning?.userRewardTransactionId ?? null
            },
          }),
        },
      }),
    [rewardId, stableIsAuthenticated, enabled],
  )

  const [state, send] = useMachine(machine, {
    input: { rewardId, isAuthenticated: stableIsAuthenticated },
  })

  // useQuery で購読した最新データをマージしてフレッシュな特典情報を生成
  // fetchQuery と同じキャッシュを共有するため重複リクエストは発生しない
  const freshReward = useMemo(() => {
    if (!rewardDetailData) return null
    return mergeRewardDetail(rewardDetailData, userRewardDetailData ?? null).reward
  }, [rewardDetailData, userRewardDetailData])

  const freshUserRewardStatus = useMemo((): UserRewardStatusDetailData | null => {
    if (!freshReward) return null
    return {
      id: freshReward.id,
      transactionId: userRewardDetailData?.reward?.transactionId ?? null,
      name: freshReward.name,
      image: freshReward.image,
      status: userRewardDetailData?.reward?.status ?? freshReward.acquisitionStatus ?? 'UNOBTAINED',
      conditions: freshReward.conditions,
      createdAt: null,
      prizes: userRewardDetailData?.reward?.prizes ?? [],
      prizeSelectionType: userRewardDetailData?.reward?.prizeSelectionType ?? null,
      isRepeatable: userRewardDetailData?.reward?.isRepeatable ?? false,
    }
  }, [freshReward, userRewardDetailData])

  // render 中に ref を直接更新することで、XState ガードが次回の評価時に必ず最新の
  // userRewardStatus を参照できるようにする。
  // useEffect では非同期になりガード評価より後に更新される可能性があるため、
  // render サイクル内での直接代入（副作用）が必要。React 18 Concurrent Mode では
  // レンダーが破棄・再起動される場合があるが、ref の最終値は常に最後にコミットされた
  // レンダーの値になるため、ガードの正確性は保たれる。
  freshUserRewardStatusRef.current = freshUserRewardStatus
  freshRewardRef.current = freshReward

  // ARカメラ使用中はページリロードを抑止する。
  // arNavigate state に侵入した際に suppressPageReload() を呼び、
  // 離脱（または アンマウント）時に restorePageReload() と
  // clearShouldReloadAfterRefresh() を呼んでフラグを解除する。
  // これにより、arNavigate 中に 401 → token/refresh が発生しても
  // _app.tsx の window.location.reload() がスキップされる。
  const suppressPageReload = useTokenStore((s) => s.suppressPageReload)
  const restorePageReload = useTokenStore((s) => s.restorePageReload)
  const clearShouldReloadAfterRefresh = useTokenStore((s) => s.clearShouldReloadAfterRefresh)
  const isArCameraActive = state.matches('arNavigate')
  useEffect(() => {
    if (!isArCameraActive) return
    suppressPageReload()
    return () => {
      restorePageReload()
      // ARカメラ使用中に 401 が発生していた場合、リロードフラグを解除する。
      // onError で状態機械側は既にフレームなしで継続済みのため、追加リロードは不要。
      clearShouldReloadAfterRefresh()
    }
  }, [isArCameraActive, suppressPageReload, restorePageReload, clearShouldReloadAfterRefresh])

  return {
    state,
    send,
    reward: freshReward ?? state.context.reward,
    userRewardStatus: freshUserRewardStatus ?? state.context.userRewardStatus,
    transaction: state.context.transaction,
    errorMessage: state.context.errorMessage,
    serverValidationErrors: state.context.serverValidationErrors,
    pendingFormData: state.context.pendingFormData,
    applicationForm: state.context.applicationForm,
    applicationFormTransactionId: state.context.applicationFormTransactionId,
    completedVia: state.context.completedVia,
    pendingSpotId: state.context.pendingSpotId,
    isLoading: state.matches('acquiring') || state.matches('exchanging') || state.matches('submittingApplicationForm'),
    isLoginRequired: state.matches('loginRequired'),
    // userRewardDetailData の初回ローディング中（キャッシュなし）にボタンスケルトンを表示するためのフラグ
    isUserStatusLoading: enabled && stableIsAuthenticated && isUserRewardDetailLoading,
    arPhotoFrames: state.context.arPhotoFrames,
    arPhotoFramesReady: state.context.arPhotoFramesReady,
    /**
     * ロード完了後、reward が null だった場合は true。
     * 以下のいずれの場合も true になり、キャンペーントップへのリダイレクトが発火する：
     * - 期間外・存在しない特典への URL 直アクセス（API が reward: null または 404 を返す）
     * - ネットワークエラー・5xx 等で loadData が失敗した場合（onError パスで reward が null のまま idle に遷移）
     * initialLoading 中は常に false（ロード完了を待ってから判定する）。
     */
    isPeriodOut: !state.matches('initialLoading') && (freshReward ?? state.context.reward) === null,
  }
}
